import { useState } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { videos } from '@/mocks/videos';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

export default function AboutPage() {
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  useDocumentTitle({
    title: '关于我们 - 成人快手',
    description:
      '成人快手是面向 18 岁以上成年用户的短视频娱乐平台，汇聚生活、娱乐、兴趣等多领域成人主题短视频，支持浏览、搜索、筛选与收藏。',
    keywords: '成人快手,关于我们,成人短视频平台,短视频娱乐',
  });

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-10">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

      {/* Hero */}
      <section className="mt-5 overflow-hidden rounded-xl border border-background-200/70 bg-background-100 p-6 md:p-10">
        <span className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary-500/15 px-3 py-1 text-xs font-medium text-primary-300">
          <i className="ri-shield-check-line" />
          仅面向 18 周岁及以上用户
        </span>
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
          关于成人快手
        </h1>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
          成人快手是一个专注于成人人群的短视频平台，内容涵盖生活娱乐、兴趣爱好等多个成人相关领域。
          我们希望用丰富的内容分类和精准的筛选，帮助每一位成年用户快速找到感兴趣的视频，并享受流畅的浏览与互动体验。
        </p>

        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
          <div className="rounded-lg border border-background-200/70 bg-background-50 p-4">
            <p className="font-heading text-2xl font-bold text-primary-300">{videos.length}+</p>
            <p className="mt-1 text-xs text-foreground-500">精选视频</p>
          </div>
          <div className="rounded-lg border border-background-200/70 bg-background-50 p-4">
            <p className="font-heading text-2xl font-bold text-accent-400">{categories.length}</p>
            <p className="mt-1 text-xs text-foreground-500">内容分类</p>
          </div>
          <div className="rounded-lg border border-background-200/70 bg-background-50 p-4">
            <p className="font-heading text-2xl font-bold text-secondary-300">18+</p>
            <p className="mt-1 text-xs text-foreground-500">适龄提示</p>
          </div>
          <div className="rounded-lg border border-background-200/70 bg-background-50 p-4">
            <p className="font-heading text-2xl font-bold text-foreground-900">100%</p>
            <p className="mt-1 text-xs text-foreground-500">前端演示</p>
          </div>
        </div>
      </section>

      {/* Content scope */}
      <section className="mt-10">
        <h2 className="font-heading text-xl font-bold text-foreground-950">内容范围</h2>
        <p className="mt-2 text-sm text-foreground-500">
          平台围绕成人用户的兴趣，提供多样化的短视频内容分类。
        </p>
        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {categories.map((c) => (
            <Link
              key={c.id}
              to={`/category/${c.id}`}
              className="group flex items-center gap-3 rounded-lg border border-background-200/70 bg-background-100 p-4 transition-colors hover:border-primary-700"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-500/15 text-secondary-300 transition-colors group-hover:bg-primary-500/20 group-hover:text-primary-300">
                <i className={`${c.icon} text-lg`} />
              </span>
              <div>
                <p className="text-sm font-semibold text-foreground-950">{c.name}</p>
                <p className="text-xs text-foreground-500">{c.desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Usage guide */}
      <section className="mt-10">
        <h2 className="font-heading text-xl font-bold text-foreground-950">使用说明</h2>
        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="rounded-lg border border-background-200/70 bg-background-100 p-5">
            <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500/15 text-primary-300">
              <i className="ri-search-line text-xl" />
            </span>
            <h3 className="font-heading text-base font-semibold text-foreground-950">搜索与筛选</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              通过顶部搜索框输入关键词，按标题、标签或作者查找内容；在分类页可按地区、时长与发布时间进一步筛选。
            </p>
          </div>
          <div className="rounded-lg border border-background-200/70 bg-background-100 p-5">
            <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-lg bg-accent-500/15 text-accent-400">
              <i className="ri-heart-3-line text-xl" />
            </span>
            <h3 className="font-heading text-base font-semibold text-foreground-950">收藏与管理</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              在视频卡片或详情页点击爱心即可收藏，收藏记录保存在你的本机浏览器中，可随时在我的收藏页移除或清空。
            </p>
          </div>
          <div className="rounded-lg border border-background-200/70 bg-background-100 p-5">
            <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-lg bg-secondary-500/15 text-secondary-300">
              <i className="ri-play-circle-line text-xl" />
            </span>
            <h3 className="font-heading text-base font-semibold text-foreground-950">观看与推荐</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              详情页提供播放演示与视频简介，并根据当前视频分类推荐同类热门内容，方便继续探索。
            </p>
          </div>
        </div>
      </section>

      {/* Feedback / contact (demo) */}
      <section className="mt-10">
        <div className="flex flex-col items-start justify-between gap-5 rounded-xl border border-primary-800/60 bg-primary-950 p-6 md:flex-row md:items-center md:p-8">
          <div className="max-w-xl">
            <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
              联系我们与反馈
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-700">
              本站为前端功能演示站点，未提供真实联系方式与在线提交入口。以下为说明性信息，仅用于展示页面结构。
            </p>
            <div className="mt-4 flex flex-wrap gap-4 text-sm text-foreground-700">
              <span className="flex items-center gap-2">
                <i className="ri-mail-line text-accent-400" />
                演示邮箱：demo@example.com（非真实）
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-time-line text-accent-400" />
                服务时间说明：以页面展示为准
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setFeedbackOpen(true)}
            className="flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-foreground-950 transition-colors hover:bg-primary-400"
          >
            <i className="ri-chat-3-line text-lg" />
            反馈入口
          </button>
        </div>
      </section>

      {feedbackOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4"
          onClick={() => setFeedbackOpen(false)}
        >
          <div
            className="w-full max-w-md animate-fade-up rounded-xl border border-background-200 bg-background-100 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-heading text-lg font-bold text-foreground-950">反馈入口</h3>
              <button
                type="button"
                onClick={() => setFeedbackOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-background-200 text-foreground-700"
                aria-label="关闭"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <p className="text-sm leading-relaxed text-foreground-600">
              当前站点为前端演示版本，反馈提交功能尚未接入后端服务，因此暂不提供在线提交。你可以在后续连接后端后，再开启真实的反馈与联系功能。
            </p>
            <button
              type="button"
              onClick={() => setFeedbackOpen(false)}
              className="mt-5 w-full rounded-full bg-primary-500 py-3 text-sm font-semibold text-foreground-950 transition-colors hover:bg-primary-400"
            >
              我知道了
            </button>
          </div>
        </div>
      )}
    </div>
  );
}